import React, { useEffect, useMemo, useState } from 'react';
import { Box, SxProps, Typography } from '@mui/material';
import { TableV2 } from 'components/Table/TableV2';
import { Loading } from 'common/ui/Loading';
import { SearchInput } from 'common/ui/SearchInput';
import {
  useGetCandidateBatchGroupByOrganizationQuery,
  useApplyCandidateBatchGroupMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useDrawer } from 'services/drawer/hooks';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import {
  CandidateBatchGroup,
  ModelSortDirection
} from 'generated-graphql/types';
import { getRulesetsListColumns } from '../utils/getRulesetsListColumns';
import { searchCandidateBatchGroups } from '../utils/searchCandidateBatchGroups';
import { ToastType, useToast } from 'services/toast';
import { Button } from 'system/atoms';
import { AlertCard } from 'common/ui';
import { Paths } from 'services/navigation/paths';
import LaunchIcon from '@mui/icons-material/Launch';

const container: SxProps = {
  width: '750px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px'
};

const searchContainer: SxProps = {
  width: '40%'
};

const buttonContainer: SxProps = {
  display: 'flex',
  gap: '16px',
  marginTop: '24px'
};

type Props = {
  candidateBatchIds: string[];
  initialCandidateBatchGroup: Pick<CandidateBatchGroup, 'id' | 'name'>;
  hasDrafts?: boolean;
  onSuccess?: () => void;
};

export const RulesetsListDrawer: React.FC<Props> = ({
  candidateBatchIds,
  initialCandidateBatchGroup,
  hasDrafts,

  onSuccess
}: {
  candidateBatchIds: string[];
  initialCandidateBatchGroup: Pick<CandidateBatchGroup, 'id' | 'name'>;
  hasDrafts?: boolean;
  onSuccess?: (
    candidateBatchGroup: Pick<CandidateBatchGroup, 'id' | 'name'>
  ) => void;
}) => {
  const organization = useOrganization();
  const { closeDrawer } = useDrawer();
  const { showToast } = useToast();
  const [searchValue, setSearchValue] = useState('');
  const [selectedRuleset, setSelectedRuleset] = useState<Pick<
    CandidateBatchGroup,
    'id' | 'name'
  > | null>(null);

  const { data, loading, error } = useGetCandidateBatchGroupByOrganizationQuery(
    {
      variables: {
        organizationId: organization.id,
        sortDirection: ModelSortDirection.Desc,
        limit: 100
      }
    }
  );

  useEffect(() => {
    if (initialCandidateBatchGroup) {
      setSelectedRuleset(initialCandidateBatchGroup);
    }
  }, [initialCandidateBatchGroup?.id]);

  const [applyCandidateBatchGroup, { loading: isApplying }] =
    useApplyCandidateBatchGroupMutation();

  const candidateBatchGroups = extractQueryListItems(data);

  const filteredRulesets = useMemo(() => {
    return searchCandidateBatchGroups(candidateBatchGroups, searchValue);
  }, [candidateBatchGroups, searchValue]);

  const columns = getRulesetsListColumns();

  const handleApplyRuleset = async () => {
    if (selectedRuleset?.id === initialCandidateBatchGroup?.id) {
      closeDrawer();
      return;
    }

    if (!selectedRuleset?.id) {
      showToast({
        message: 'Please select a ruleset to apply',
        type: ToastType.Error
      });
      return;
    }

    try {
      await applyCandidateBatchGroup({
        variables: {
          input: {
            candidateBatchGroupId: selectedRuleset.id,
            candidateBatchIds
          }
        }
      });

      onSuccess?.(selectedRuleset);

      showToast({
        title: `${selectedRuleset.name} ruleset applied`,
        message: 'Applicants are being re-ranked now.',
        type: ToastType.Success
      });

      closeDrawer();
    } catch (error) {
      showToast({ message: 'Failed to apply ruleset', type: ToastType.Error });
    }
  };

  const handleRedirectToCreateRuleset = () =>
    window.open(Paths.ANALYSE_RULESET_CREATE, '_blank');

  const handleCancel = () => {
    closeDrawer();
  };

  if (loading) {
    return (
      <Box sx={container}>
        <Loading />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={container}>
        <AlertCard type="error" title="Error">
          Failed to load rulesets. Please try again.
        </AlertCard>
      </Box>
    );
  }

  const isEmpty =
    data?.getCandidateBatchGroupByOrganization?.items?.length === 0;

  return (
    <Box sx={container}>
      <Box>
        <Typography variant="sectionTitle">Choose ruleset</Typography>
      </Box>

      <Box sx={searchContainer}>
        <SearchInput placeholder="Search" onChange={setSearchValue} />
      </Box>

      <TableV2
        data={filteredRulesets}
        columns={columns}
        isFiltering={!!searchValue.trim()}
        pagination
        selectableRowsSingle
        selectableRows
        selectableRowSelected={(row) => row.id === selectedRuleset?.id}
        onSelectedRowsChange={(state) => {
          setSelectedRuleset(state.selectedRows[0]);
        }}
        emptyStateComponent={
          isEmpty ? (
            <Typography variant="heading">No rulesets created</Typography>
          ) : null
        }
      />

      <AlertCard
        type="info"
        title="Candidates are re-analysed once the ruleset is applied"
      >
        When a new ruleset is applied to an existing analysis, applicants will
        be re-ranked immediately.
      </AlertCard>

      {hasDrafts && (
        <AlertCard
          type="warning"
          title="Draft analyses won't have rulesets applied"
        />
      )}

      <Box sx={buttonContainer}>
        <Button
          variant="primary"
          onClick={isEmpty ? handleRedirectToCreateRuleset : handleApplyRuleset}
          loading={isApplying}
          disabled={isApplying}
          endIcon={isEmpty ? <LaunchIcon /> : undefined}
        >
          {isEmpty ? 'Create ruleset' : 'Apply ruleset'}
        </Button>
        <Button onClick={handleCancel}>Cancel</Button>
      </Box>
    </Box>
  );
};
